import PQueue from 'p-queue';

import pluggableApisService from 'services/pluggableApis';

import { renderEffect } from './enhanceEffectProcess';
import { renderBlurredBackground, loadImage } from './backgroundBlurProcess';
import type { IProcessResult } from './toolchain';

export interface ComposedEnhanceAttrs {
  enhanceEffectId?: string | null;
  enhanceIntensity?: number;
  blurEffectId?: string | null;
  blurIntensity?: number;
}

// Queue only for warp_fx rendering (GPU-bound), not for bg-remove API calls
const renderQueue = new PQueue({ concurrency: 1 });

// Cache bg-remove masks so the API is called only once per item
const maskCache = new Map<string, string>();
const pendingMasks = new Map<string, Promise<string>>();

export function cancelComposedQueue() {
  renderQueue.clear();
}

export function clearMaskCache() {
  maskCache.clear();
  pendingMasks.clear();
}

async function getCachedMask(itemKey: string, isCancel: () => boolean): Promise<string> {
  const cached = maskCache.get(itemKey);
  if (cached) return cached;

  const pending = pendingMasks.get(itemKey);
  if (pending) return pending;

  const promise = pluggableApisService
    .backgroundRemove(itemKey, isCancel, { skipAsyncJob: true })
    .then(url => {
      maskCache.set(itemKey, url);
      pendingMasks.delete(itemKey);
      return url;
    })
    .catch(err => {
      pendingMasks.delete(itemKey);
      throw err;
    });

  pendingMasks.set(itemKey, promise);
  return promise;
}

export function getComposedEnhanceProcess({ enhanceEffectId, enhanceIntensity, blurEffectId, blurIntensity }: ComposedEnhanceAttrs) {
  return async (image: HTMLImageElement, isCancel: () => boolean, itemKey?: string): Promise<IProcessResult> => {
    // Phase 1: Background remove runs outside the render queue so multiple items
    // are processed in parallel (concurrency managed by the API service queue)
    let maskedImageUrl: string | undefined;
    if (blurEffectId) {
      maskedImageUrl = await getCachedMask(itemKey as string, isCancel);
      if (isCancel()) throw new Error('cancelled');
    }

    // Phase 2: warp_fx rendering is queued (concurrency: 1) to avoid GPU contention
    const result = await renderQueue.add(
      async () => {
        if (isCancel()) throw new Error('cancelled');

        const width = image.naturalWidth;
        const height = image.naturalHeight;
        let finalCanvas: HTMLCanvasElement | null = null;

        // Step 1: Apply background blur if selected (blur first so smart adjustment applies uniformly on top)
        if (blurEffectId && maskedImageUrl) {
          const blurredCanvas = await renderBlurredBackground(image.src, width, height, blurEffectId, blurIntensity || 50);
          if (isCancel()) throw new Error('cancelled');

          const foregroundImg = await loadImage(maskedImageUrl);
          const ctx = blurredCanvas.getContext('2d')!;
          ctx.drawImage(foregroundImg, 0, 0, blurredCanvas.width, blurredCanvas.height);
          finalCanvas = blurredCanvas;
        }

        // Step 2: Apply smart adjustment on top of the composited result (or original if no blur)
        if (enhanceEffectId) {
          let sourceSrc = image.src;
          if (finalCanvas) {
            const blob = await new Promise<Blob>(res => finalCanvas!.toBlob(b => res(b!), 'image/webp'));
            sourceSrc = URL.createObjectURL(blob);
          }

          finalCanvas = await renderEffect(sourceSrc, width, height, enhanceEffectId, enhanceIntensity || 50);

          if (sourceSrc !== image.src) {
            URL.revokeObjectURL(sourceSrc);
          }
        }

        if (!finalCanvas) {
          throw new Error('No effects to apply');
        }

        const blob = await new Promise<Blob>(res => finalCanvas!.toBlob(b => res(b!), 'image/webp'));
        return { blob, result: URL.createObjectURL(blob) };
      },
      { throwOnTimeout: true },
    );

    return result as IProcessResult;
  };
}
